import { Avatar, Card, Flex, Icon, Loader, Paginator, Text, Title } from '@upsales/components';
import { useTranslation } from 'Components/Helpers/translate';
import BemClass from '@upsales/components/Utils/bemClass';
import { SlideFade } from '@upsales/components/animations';
import { useGetData, useIsFirstRender } from 'App/components/hooks';
import RequestBuilder from 'Resources/RequestBuilder';
import moment from 'moment';
import React, { useEffect, useRef, useState, type ReactNode } from 'react';

import './ChangeLog.scss';

const getUser = (userId: number) => {
	const users = (window as any).Tools?.AppService?.getUsers?.(undefined, true) || [];
	return users.find((u: any) => u.id === userId);
};

export type ChangeLogEntry<T = any> = {
	version?: number | string;
	id?: number | string;
	modDate: string | Date;
	modBy: number | { id: number; name: string; email?: string };
	source?: { type: string; name?: string };
	changes?: T[];
	[key: string]: any;
};

type ChangeLogProps<T = any> = {
	title?: string;
	fetcher: (
		rb: RequestBuilder,
		fetcherProps: any
	) => Promise<{ data: any[]; metadata: { total: number; limit: number; offset: number } }>;
	fetcherProps: any;
	limit?: number;
	renderChangeDetails?: (change: ChangeLogEntry<T>, index: number) => ReactNode;
	renderChangeSummary?: (change: ChangeLogEntry<T>) => ReactNode;
	getSourceString?: (source: { type: string; name?: string }, translateFn: (key: string) => string) => string | null;
	filters?: ReactNode;
	emptyMessage?: string;
	className?: string;
	onDataChange?: (change: ChangeLogEntry<T>) => void;
};

const ChangeLogEntryCard = <T,>({
	change,
	index,
	renderChangeDetails,
	renderChangeSummary,
	getSourceString,
	className
}: {
	change: ChangeLogEntry<T>;
	index: number;
	renderChangeDetails?: (change: ChangeLogEntry<T>, index: number) => ReactNode;
	renderChangeSummary?: (change: ChangeLogEntry<T>) => ReactNode;
	getSourceString?: (source: { type: string; name?: string }, translateFn: (key: string) => string) => string | null;
	className?: string;
}) => {
	const classes = new BemClass(className || 'ChangeLog');
	const { t } = useTranslation();
	const [showDropDown, setShowDropDown] = useState(false);

	const modBy =
		typeof change.modBy === 'number'
			? getUser(change.modBy) ?? { name: t('default.unknown'), email: '' }
			: change.modBy ?? { name: t('default.unknown'), email: '' };

	const dropdownRef = useRef<HTMLDivElement>(null);
	const dropdownHeight = dropdownRef.current?.clientHeight;

	const hasChanges = change.changes && change.changes.length > 0;
	const changesCount =
		change.changes?.length === 1
			? t('auditLog.showOneChange')
			: change.changes?.length
			? t('auditLog.showXChanges', { count: change.changes.length })
			: null;

	return (
		<Card borderRadius>
			<Flex
				onClick={() => setShowDropDown(prev => !prev)}
				className={classes.elem('preview').b()}
				justifyContent="space-between"
				alignItems="center"
				space="pll prl"
			>
				<Flex alignItems="center" gap="u2">
					{modBy ? (
						<>
							<Avatar initials={modBy.name} email={modBy.email}></Avatar>
							<Flex gap="u1" direction="column">
								<Flex direction="row" gap="u1" alignItems="center">
									<Text>{modBy.name}</Text>
									{change.source && getSourceString ? (
										<Text size="sm" color="grey-11">
											{getSourceString(change.source, t) ?? ''}
										</Text>
									) : null}
								</Flex>
								<Text size="sm" color="grey-11">
									{moment(change.modDate).format('L LT')}
								</Text>
							</Flex>
						</>
					) : null}
				</Flex>
				<Flex alignItems="center" gap="u2">
					{renderChangeSummary ? (
						renderChangeSummary(change)
					) : changesCount ? (
						<Text>{changesCount}</Text>
					) : null}
					{hasChanges && renderChangeDetails ? (
						<Icon name={showDropDown ? 'chevron-up' : 'chevron-down'} />
					) : null}
				</Flex>
			</Flex>

			{hasChanges && renderChangeDetails ? (
				<SlideFade visible={showDropDown} height maxHeight={dropdownHeight}>
					<div>
						<div ref={dropdownRef}>{renderChangeDetails(change, index)}</div>
					</div>
				</SlideFade>
			) : null}
		</Card>
	);
};

const ChangeLog = <T,>({
	title,
	fetcher,
	fetcherProps,
	limit = 10,
	renderChangeDetails,
	renderChangeSummary,
	getSourceString,
	filters,
	emptyMessage,
	className,
	onDataChange
}: ChangeLogProps<T>) => {
	const classes = new BemClass(className || 'ChangeLog');
	const { t } = useTranslation();

	const [offset, setOffset] = useState(0);

	const { data, loading, metadata, refetch } = useGetData<any, ChangeLogEntry<T>>({
		fetcher: (rb: RequestBuilder) => fetcher(rb, fetcherProps),
		fetcherProps,
		limit,
		offset
	});

	const isFirstRender = useIsFirstRender();
	useEffect(() => {
		if (!isFirstRender) {
			refetch();
		}
	}, [offset, fetcherProps]);

	useEffect(() => {
		if (onDataChange && data) {
			data.forEach(change => onDataChange(change));
		}
	}, [data]);

	return (
		<Flex className={classes.b()} gap="u3" direction="column">
			{(title || filters) && (
				<Flex gap="u1" alignItems="center" className={classes.elem('filters').b()}>
					{title && <Title space="mrm">{title}</Title>}
					{filters}
				</Flex>
			)}
			{loading ? (
				<Flex className={classes.b()} justifyContent="center">
					<Loader size="md" />
				</Flex>
			) : data.length === 0 && !loading ? (
				<Title italic space="mtxl" align="center" color="grey-11">
					{emptyMessage || t('auditLog.noChanges')}
				</Title>
			) : (
				data.map((change, index) => (
					<ChangeLogEntryCard
						key={change.version ?? change.id ?? index}
						change={change}
						index={index}
						renderChangeDetails={renderChangeDetails}
						renderChangeSummary={renderChangeSummary}
						getSourceString={getSourceString}
						className={className}
					/>
				))
			)}

			{metadata.total > (metadata.limit ?? 0) ? (
				<Paginator
					limit={metadata.limit ?? 0}
					offset={offset}
					total={metadata.total}
					onChange={setOffset}
					align="center"
				/>
			) : null}
		</Flex>
	);
};

export default ChangeLog;
export type { ChangeLogProps };
